// Copied verbatim from Cloudflare-Worker workers/dashboard-worker/ui/src/lib/experiment-stats.js
// (branch feat/ab-testing). The A/B Testing campaign type duplicates it on purpose rather than
// sharing a package; if you fix a bug here, fix it there too.

/**
 * A/B testing statistical helpers — Phase 4.
 *
 * Pure JS, zero dependencies. All functions take impressions and
 * conversions in the same units the dashboard's metrics endpoint emits
 * (rows from experiment_metrics_daily, aggregated client-side per
 * variant in ExperimentDrawer).
 *
 * Two analyses are offered side-by-side so operators can sanity-check
 * one against the other:
 *
 *   - twoProportionZTest: classic frequentist; pooled SE; α = 0.05.
 *     Cheap, deterministic, and what most reference tables use.
 *   - betaBinomialPosterior: Bayesian Beta-Binomial posterior via Monte
 *     Carlo. Robust under continuous monitoring (no "p-value peeking"
 *     problem) and produces a probability the variant beats control,
 *     plus a 95 % lift CI.
 *
 * Wilson intervals are used for per-variant rate CIs — better small-N
 * behavior than the Normal-approximation interval that's everywhere
 * else in the wild.
 */

// =============================================================================
// CONSTANTS
// =============================================================================

const Z95 = 1.959963984540054; // 1 - Φ⁻¹(0.025), the standard 95 % CI multiplier.
const DEFAULT_MIN_CONVERSIONS = 30;

// =============================================================================
// NORMAL CDF (Abramowitz & Stegun 7.1.26)
// =============================================================================

/**
 * Error function via A&S 7.1.26 polynomial approximation. Max error ~1.5e-7
 * — plenty for p-value display. Symmetric: erf(-x) = -erf(x).
 */
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const a1 =  0.254829592, a2 = -0.284496736, a3 =  1.421413741,
        a4 = -1.453152027, a5 =  1.061405429, p  =  0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}

/**
 * Standard normal CDF Φ(x) = P(Z ≤ x).
 */
function normalCdf(x) {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

// =============================================================================
// TWO-PROPORTION Z-TEST
// =============================================================================

/**
 * Two-sided two-proportion z-test with pooled standard error.
 *
 * @param {number} cConv  conversions in control
 * @param {number} cN     impressions in control
 * @param {number} vConv  conversions in variant
 * @param {number} vN     impressions in variant
 * @returns {{z: number, pValue: number, significant: boolean,
 *            controlRate: number, variantRate: number,
 *            absoluteLift: number, relativeLift: number|null}|null}
 *          Returns null when SE is zero (e.g. zero impressions in either group).
 */
export function twoProportionZTest(cConv, cN, vConv, vN) {
  if (!Number.isFinite(cN) || !Number.isFinite(vN) || cN <= 0 || vN <= 0) return null;
  if (cConv < 0 || vConv < 0 || cConv > cN || vConv > vN) return null;

  const pCtrl = cConv / cN;
  const pVar  = vConv / vN;
  const pPool = (cConv + vConv) / (cN + vN);
  const se = Math.sqrt(pPool * (1 - pPool) * (1 / cN + 1 / vN));

  if (se === 0) {
    return {
      z: 0,
      pValue: 1,
      significant: false,
      controlRate: pCtrl,
      variantRate: pVar,
      absoluteLift: pVar - pCtrl,
      relativeLift: pCtrl > 0 ? (pVar - pCtrl) / pCtrl : null,
    };
  }

  const z = (pVar - pCtrl) / se;
  const pValue = 2 * (1 - normalCdf(Math.abs(z)));

  return {
    z,
    pValue,
    significant: pValue < 0.05,
    controlRate: pCtrl,
    variantRate: pVar,
    absoluteLift: pVar - pCtrl,
    relativeLift: pCtrl > 0 ? (pVar - pCtrl) / pCtrl : null,
  };
}

// =============================================================================
// BAYESIAN BETA-BINOMIAL POSTERIOR
// =============================================================================

/**
 * Standard normal sample via Box-Muller. We only need one variate per
 * call so we discard the paired output — the bookkeeping for a cache
 * isn't worth it at our iteration counts.
 */
function sampleStandardNormal() {
  // Math.random() can technically return 0; Math.log(0) is -Infinity.
  // Clamp away from zero with a tiny epsilon.
  const u1 = Math.max(Math.random(), 1e-300);
  const u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * Sample from Gamma(shape, 1). Marsaglia & Tsang (2000) for shape ≥ 1;
 * Stuart's boost for 0 < shape < 1 (returns Gamma(shape+1) * U^(1/shape)).
 *
 * Marsaglia-Tsang has near-100 % acceptance, ~one normal + one uniform
 * per draw in the common case — fast enough for 10k posterior samples.
 */
function sampleGamma(shape) {
  if (shape <= 0) return 0;
  if (shape < 1) {
    // Boost: Gamma(α) = Gamma(α+1) * U^(1/α), where U ~ Uniform(0, 1).
    const u = Math.max(Math.random(), 1e-300);
    return sampleGamma(shape + 1) * Math.pow(u, 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  while (true) {
    let x, v;
    do {
      x = sampleStandardNormal();
      v = 1 + c * x;
    } while (v <= 0);
    v = v * v * v;
    const u = Math.random();
    if (u < 1 - 0.0331 * x * x * x * x) return d * v;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v;
  }
}

/**
 * Sample from Beta(α, β) by α / (α + β'), where α and β' are
 * independent Gamma(α, 1) and Gamma(β, 1) variates.
 */
function sampleBeta(alpha, beta) {
  const x = sampleGamma(alpha);
  const y = sampleGamma(beta);
  return x / (x + y);
}

/**
 * Empirical 2.5th and 97.5th percentiles of a sorted-numeric array.
 */
function percentile95CI(sortedAsc) {
  const n = sortedAsc.length;
  if (n < 2) return [null, null];
  const lo = sortedAsc[Math.floor(0.025 * n)];
  const hi = sortedAsc[Math.min(n - 1, Math.ceil(0.975 * n) - 1)];
  return [lo, hi];
}

/**
 * Bayesian Beta-Binomial posterior comparison via Monte Carlo.
 *
 * Prior: Beta(1, 1) = Uniform(0, 1). Posterior given k successes in n
 * trials: Beta(1 + k, 1 + n - k). We draw `iters` paired samples of
 * (controlRate, variantRate) and report:
 *
 *   - probVariantBeatsControl: P(rate_var > rate_ctrl | data).
 *   - lift95: empirical 95 % CI for relative lift (rate_var - rate_ctrl)
 *             / rate_ctrl, with NaN drops for samples where the control
 *             rate happened to be exactly zero.
 *
 * @param {number} cConv conversions in control
 * @param {number} cN    impressions in control
 * @param {number} vConv conversions in variant
 * @param {number} vN    impressions in variant
 * @param {number} [iters=10000]
 * @returns {{probVariantBeatsControl: number, lift95: [number|null, number|null]}|null}
 */
export function betaBinomialPosterior(cConv, cN, vConv, vN, iters = 10000) {
  if (!Number.isFinite(cN) || !Number.isFinite(vN) || cN < 0 || vN < 0) return null;
  if (cConv < 0 || vConv < 0 || cConv > cN || vConv > vN) return null;

  const ctrlAlpha = 1 + cConv;
  const ctrlBeta  = 1 + cN - cConv;
  const varAlpha  = 1 + vConv;
  const varBeta   = 1 + vN - vConv;

  let wins = 0;
  const lifts = [];
  for (let i = 0; i < iters; i++) {
    const rC = sampleBeta(ctrlAlpha, ctrlBeta);
    const rV = sampleBeta(varAlpha, varBeta);
    if (rV > rC) wins++;
    if (rC > 0) lifts.push((rV - rC) / rC);
  }
  lifts.sort((a, b) => a - b);
  return {
    probVariantBeatsControl: wins / iters,
    lift95: percentile95CI(lifts),
  };
}

// =============================================================================
// WILSON CONFIDENCE INTERVAL
// =============================================================================

/**
 * Wilson score interval for a binomial proportion. Better small-sample
 * behavior than the textbook Normal-approximation interval and avoids
 * the [0, 1] clipping issue for extreme rates.
 *
 * @param {number} conv conversions
 * @param {number} n    impressions
 * @param {number} [z=Z95] standard-normal critical value (1.96 for 95%)
 * @returns {[number, number]} [lo, hi] in [0, 1]
 */
export function wilsonInterval(conv, n, z = Z95) {
  if (!Number.isFinite(n) || n <= 0 || conv < 0 || conv > n) return [0, 0];
  const p = conv / n;
  const denom = 1 + (z * z) / n;
  const center = (p + (z * z) / (2 * n)) / denom;
  const margin = (z / denom) * Math.sqrt(p * (1 - p) / n + (z * z) / (4 * n * n));
  return [Math.max(0, center - margin), Math.min(1, center + margin)];
}

// =============================================================================
// SAMPLE-SIZE GUARD
// =============================================================================

/**
 * Check whether every supplied variant total meets the minimum-conversion
 * threshold. Below the threshold, the z-test's normal-approximation
 * breaks down and operators shouldn't be making winner decisions yet.
 *
 * @param {Object.<string, {conversions: number}>} totalsByVariant
 *        Map keyed by variant key, values having a `conversions` count.
 * @param {number} [minConversions=30]
 * @returns {{ok: boolean, byVariant: Object.<string, boolean>}}
 */
export function meetsMinSample(totalsByVariant, minConversions = DEFAULT_MIN_CONVERSIONS) {
  const byVariant = {};
  let ok = Object.keys(totalsByVariant).length > 0;
  for (const [variant, t] of Object.entries(totalsByVariant)) {
    const meets = (t?.conversions || 0) >= minConversions;
    byVariant[variant] = meets;
    if (!meets) ok = false;
  }
  return { ok, byVariant };
}

// =============================================================================
// COMBINED PER-VARIANT VIEW
// =============================================================================

/**
 * Convenience: for a single non-control variant, compute everything the
 * UI needs in one call. Returns null when control row is missing (no
 * comparison possible).
 *
 * The control variant for a row gets `null` for `zTest` / `bayes` /
 * `lift` since we never compare control against itself.
 */
export function variantStatsVsControl(controlTotals, variantTotals, opts = {}) {
  const out = {
    rate: 0,
    rateCi: [0, 0],
    impressions: variantTotals?.impressions || 0,
    conversions: variantTotals?.conversions || 0,
    zTest: null,
    bayes: null,
  };
  out.rate   = out.impressions > 0 ? out.conversions / out.impressions : 0;
  out.rateCi = wilsonInterval(out.conversions, out.impressions);
  if (!controlTotals) return out;
  out.zTest = twoProportionZTest(
    controlTotals.conversions || 0, controlTotals.impressions || 0,
    out.conversions,               out.impressions
  );
  out.bayes = betaBinomialPosterior(
    controlTotals.conversions || 0, controlTotals.impressions || 0,
    out.conversions,               out.impressions,
    opts.bayesIters || 10000
  );
  return out;
}
